import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Table_Styling = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🌈 CSS Table Styling</h1>

      <p className="tutorial-intro">
        CSS allows you to style tables to make them more attractive and
        user-friendly. You can customize width, height, backgrounds, text, and
        even add hover effects for better readability.
      </p>

      {/* Why Style Tables Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          ✨ Why Style Tables?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Styled tables improve readability, highlight important data, and make
          your web pages look more professional.
        </p>
      </div>

      {/* Table Width & Height */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#2563eb" }}>
          🔹 Table Width & Height
        </h2>
        <p>
          Use <code>width</code> and <code>height</code> to control the size of
          the table.
        </p>

        <CodeCopyBlock
          code={`table {
  width: 100%;
  height: 200px;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              height: "120px",
              border: "2px solid #2563eb",
              borderCollapse: "collapse",
              background: "#dbeafe",
            }}
          >
            <tr>
              <td
                style={{
                  border: "2px solid #2563eb",
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "#1e3a8a",
                }}
              >
                Wide Table
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Table Backgrounds */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#16a34a" }}>
          🔹 Table Backgrounds
        </h2>
        <p>
          You can add background colors to the entire table, specific rows, or
          individual cells.
        </p>

        <CodeCopyBlock
          code={`table {
  background-color: #ecfdf5;
}

tr:nth-child(even) {
  background-color: #d1fae5;
}

td {
  background-color: #ffffff;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #16a34a",
              backgroundColor: "#ecfdf5",
            }}
          >
            <tr style={{ backgroundColor: "#d1fae5" }}>
              <td style={{ border: "2px solid #16a34a", padding: "8px" }}>
                Row 1
              </td>
              <td style={{ border: "2px solid #16a34a", padding: "8px" }}>
                Data A
              </td>
            </tr>
            <tr>
              <td style={{ border: "2px solid #16a34a", padding: "8px" }}>
                Row 2
              </td>
              <td style={{ border: "2px solid #16a34a", padding: "8px" }}>
                Data B
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Table Text Styling */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#dc2626" }}>
          🔹 Table Text Styling
        </h2>
        <p>
          Use <code>font-family</code>, <code>font-size</code> and{" "}
          <code>color</code> for beautiful text inside tables.
        </p>

        <CodeCopyBlock
          code={`td {
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #dc2626;
  text-align: center;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #dc2626",
            }}
          >
            <tr>
              <td
                style={{
                  border: "2px solid #dc2626",
                  fontFamily: "Arial",
                  fontSize: "14px",
                  color: "#dc2626",
                  textAlign: "center",
                  padding: "8px",
                  fontWeight: "bold",
                }}
              >
                Styled Text
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Table Hover Effect */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#9333ea" }}>
          🔹 Table Hover Effect
        </h2>
        <p>
          Add interactivity using <code>:hover</code> to highlight rows when the
          mouse hovers over them.
        </p>

        <CodeCopyBlock
          code={`tr:hover {
  background-color: #fbcfe8;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #9333ea",
            }}
          >
            <tr style={{ cursor: "pointer" }}>
              <td style={{ border: "2px solid #9333ea", padding: "8px" }}>
                Hover Me
              </td>
              <td style={{ border: "2px solid #9333ea", padding: "8px" }}>
                Highlighted Row
              </td>
            </tr>
            <tr style={{ cursor: "pointer" }}>
              <td style={{ border: "2px solid #9333ea", padding: "8px" }}>
                Another Row
              </td>
              <td style={{ border: "2px solid #9333ea", padding: "8px" }}>
                Try Hover
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Zebra Stripes */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#ea580c" }}>
          🔹 Zebra Stripes
        </h2>
        <p>
          Use <code>nth-child(even)</code> or <code>nth-child(odd)</code> to
          create alternating row colors.
        </p>

        <CodeCopyBlock
          code={`tr:nth-child(even) {
  background-color: #ffedd5;
}

tr:nth-child(odd) {
  background-color: #ffffff;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #ea580c",
            }}
          >
            <tr style={{ backgroundColor: "#ffedd5" }}>
              <td style={{ border: "2px solid #ea580c", padding: "8px" }}>
                Row 1
              </td>
            </tr>
            <tr>
              <td style={{ border: "2px solid #ea580c", padding: "8px" }}>
                Row 2
              </td>
            </tr>
            <tr style={{ backgroundColor: "#ffedd5" }}>
              <td style={{ border: "2px solid #ea580c", padding: "8px" }}>
                Row 3
              </td>
            </tr>
          </table>
        </div>
      </div>

      {/* Highlight Specific Row */}
      <div className="tutorial-section">
        <h2 className="section-title" style={{ color: "#0d9488" }}>
          🔹 Highlight Specific Row/Column
        </h2>
        <p>
          Use <code>:nth-child</code> or <code>class</code> to highlight
          important rows or columns.
        </p>

        <CodeCopyBlock
          code={`tr:nth-child(2) {
  background-color: #bfdbfe;
}

td:nth-child(1) {
  font-weight: bold;
  color: #0d9488;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "2px solid #0d9488",
            }}
          >
            <tr>
              <td
                style={{
                  border: "2px solid #0d9488",
                  padding: "8px",
                  fontWeight: "bold",
                  color: "#0d9488",
                }}
              >
                Name
              </td>
              <td style={{ border: "2px solid #0d9488", padding: "8px" }}>
                Score
              </td>
            </tr>
            <tr style={{ backgroundColor: "#bfdbfe" }}>
              <td
                style={{
                  border: "2px solid #0d9488",
                  padding: "8px",
                  fontWeight: "bold",
                  color: "#0d9488",
                }}
              >
                Alice
              </td>
              <td style={{ border: "2px solid #0d9488", padding: "8px" }}>
                90
              </td>
            </tr>
            <tr>
              <td
                style={{
                  border: "2px solid #0d9488",
                  padding: "8px",
                  fontWeight: "bold",
                  color: "#0d9488",
                }}
              >
                Bob
              </td>
              <td style={{ border: "2px solid #0d9488", padding: "8px" }}>
                85
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Table_Styling;
